// ============================================================================
// Analytics — single source of truth for ALL tracking platforms.
// ----------------------------------------------------------------------------
// Each platform's library is loaded (and bot-gated) in index.html:
//   GA4    → window.gtag        (Google Analytics)
//   Meta   → window.fbq         (Facebook / Instagram ads)
//   Bing   → window.uetq        (Microsoft ads)
//   OpenAI → window.oaiq        (ChatGPT ads)
//
// For bots NONE of these load, so every send below is a silent no-op — bot
// traffic never reaches any platform.
//
// Call sites use the funnel helpers (viewItem, beginCheckout, purchase, …) and
// each helper fans out to whichever platforms care about that event. Add or
// remove a platform in ONE place here — never at the call sites.
//
// GA4 ecommerce funnel (what makes Revenue + funnel reports work):
//   view_item_list → /hotels   view_item → property   begin_checkout → /checkout
//   add_payment_info → payment  purchase → /checkout/confirm (Revenue)
// ============================================================================

export const GA4_MEASUREMENT_ID = 'G-ZE10TMD9HF'

const num = (v) => (v !== null && v !== undefined && v !== '' ? Number(v) : undefined)

const clean = (obj) =>
  Object.fromEntries(
    Object.entries(obj || {}).filter(([, v]) => v !== null && v !== undefined && v !== '')
  )

// ---- Per-platform senders (each no-ops if its lib isn't present) -----------

function ga(name, params = {}) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', name, clean(params))
  }
}

function meta(event, params = {}) {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', event, clean(params))
  }
}

function bing(event, params = {}) {
  if (typeof window !== 'undefined' && window.uetq && typeof window.uetq.push === 'function') {
    window.uetq.push('event', event, clean(params))
  }
}

function oai(event, params = {}) {
  if (typeof window !== 'undefined' && typeof window.oaiq === 'function') {
    window.oaiq('measure', event, clean({ type: 'customer_action', ...params }))
  }
}

// ---- Payload builders ------------------------------------------------------

/** One GA4 items[] entry. */
function gaItem({ hotelId, hotelName, price, quantity, category, index } = {}) {
  return clean({
    item_id: hotelId,
    item_name: hotelName,
    item_category: category || 'hotel',
    price: num(price),
    quantity: quantity || 1,
    index,
  })
}

/** Meta content payload from a property. */
function metaContent({ hotelId, hotelName, value, currency, rooms, category } = {}) {
  return clean({
    content_ids: hotelId ? [hotelId] : undefined,
    content_type: 'product',
    content_name: hotelName,
    item_category: category,
    value: num(value),
    currency,
    num_items: rooms || undefined,
  })
}

// ---- Page views ------------------------------------------------------------

/** SPA page_view — called on every route change (App.jsx). */
export function pageview({ path, location, title } = {}) {
  ga('page_view', { page_path: path, page_location: location, page_title: title })
  meta('PageView', { page_path: path })
  // Bing tracks SPA page loads automatically (enableAutoSpaTracking).
}

// ---- Ecommerce funnel ------------------------------------------------------

/** Search results list view (/hotels). */
export function viewItemList({ items = [], listName, currency, source } = {}) {
  ga('view_item_list', {
    item_list_name: listName,
    currency,
    traffic_source: source,
    items: items.map((it, i) => gaItem({ ...it, index: i })),
  })
}

/** Property detail page view. */
export function viewItem({ hotelId, hotelName, price, currency, category, source } = {}) {
  ga('view_item', {
    currency,
    value: num(price),
    traffic_source: source,
    items: [gaItem({ hotelId, hotelName, price, category })],
  })
  meta('ViewContent', metaContent({ hotelId, hotelName, value: price, currency, category }))
}

/** A property/rate was selected from a list. */
export function selectItem({ hotelId, hotelName, price, currency, listName, category } = {}) {
  ga('select_item', {
    item_list_name: listName,
    currency,
    items: [gaItem({ hotelId, hotelName, price, category })],
  })
  meta('CustomizeProduct', metaContent({ hotelId, hotelName, value: price, currency, category }))
}

/** Reached checkout. */
export function beginCheckout({ hotelId, hotelName, value, currency, rooms, category } = {}) {
  ga('begin_checkout', {
    currency,
    value: num(value),
    items: [gaItem({ hotelId, hotelName, price: value, quantity: rooms, category })],
  })
  meta('InitiateCheckout', metaContent({ hotelId, hotelName, value, currency, rooms, category }))
  bing('begin_checkout', { revenue_value: num(value), currency })
}

/** Entered payment details. */
export function addPaymentInfo({ hotelId, hotelName, value, currency, category } = {}) {
  ga('add_payment_info', {
    currency,
    value: num(value),
    items: [gaItem({ hotelId, hotelName, price: value, category })],
  })
  meta('AddPaymentInfo', metaContent({ hotelId, hotelName, value, currency, category }))
}

/** Confirmed booking — populates Revenue across GA4 / Meta / Bing / OpenAI. */
export function purchase({ transactionId, value, currency, hotelId, hotelName, rooms, category } = {}) {
  const cur = currency || 'USD'
  const amount = num(value) ?? 0
  ga('purchase', {
    transaction_id: transactionId,
    value: amount,
    currency: cur,
    items: [gaItem({ hotelId, hotelName, price: value, quantity: rooms, category })],
  })
  meta('Purchase', {
    ...metaContent({ hotelId, hotelName, value: amount, currency: cur, rooms, category }),
    transaction_id: transactionId,
  })
  bing('purchase', { revenue_value: amount, currency: cur, transaction_id: transactionId })
  oai('Purchase', { amount, currency: cur, transaction_id: transactionId })
}

// ---- Other events ----------------------------------------------------------

/** Account created. method: 'email' | 'google' | 'phone'. */
export function trackSignUp(method = 'email') {
  ga('sign_up', { method })
  meta('CompleteRegistration', { method })
  bing('sign_up', { method })
  oai('registration_completed', { amount: 0, currency: 'USD' })
}

/** A search was run. */
export function trackSearch({ searchTerm, category, checkin, checkout, adults, rooms, currency } = {}) {
  ga('search', { search_term: searchTerm, item_category: category, checkin, checkout, adults, rooms, currency })
  meta('Search', { search_string: searchTerm, content_category: category, checkin, checkout, currency })
}

/** Engagement signal on a property page (scroll/click/time) — GA4 bounce analysis. */
export function trackHotelEngaged({ hotelId, signal, source } = {}) {
  ga('hotel_engaged', { hotel_id: hotelId, engagement_signal: signal, traffic_source: source })
}

// ---- Helpers --------------------------------------------------------------

/**
 * Where did this visit come from? Checked in order:
 *   offerId → google_hotel_ads, gclid → google_ads, utm_source, referrer host, else direct.
 */
export function detectTrafficSource(searchParams) {
  try {
    if (searchParams?.get?.('offerId')) return 'google_hotel_ads'
    if (searchParams?.get?.('gclid')) return 'google_ads'
    const utm = searchParams?.get?.('utm_source')
    if (utm) return utm.toLowerCase()
    const ref = typeof document !== 'undefined' ? document.referrer : ''
    if (!ref) return 'direct'
    const host = new URL(ref).hostname.toLowerCase()
    if (host.includes('google.')) return 'google_organic'
    if (host.includes('bing.')) return 'bing_organic'
    if (host.includes('duckduckgo.')) return 'duckduckgo_organic'
    if (host.includes('voyabay.com')) return 'internal'
    return host
  } catch {
    return 'unknown'
  }
}
